import { useState } from "react";

import { format, formatDistanceToNow } from "date-fns";
import { ArrowDownToLine, Bell, ChevronDown, Loader2 } from "lucide-react";

import { navigate } from "@redwoodjs/router";

import { StoreAvatar } from "src/components/StoreAvatar/StoreAvatar";
import { Button } from "src/components/ui/button";
import { ScrollArea } from "src/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { cn } from "src/lib/utils";

const formatTimestampTooltip = (value) => {
  if (!value) {
    return null;
  }

  return format(new Date(value), "MMM d, yyyy 'at' h:mm a");
};

const formatRelativeTimestamp = (value) => {
  if (!value) {
    return null;
  }

  return formatDistanceToNow(new Date(value), {
    addSuffix: true,
  });
};

/**
 * Routes in-app notification actions through Redwood when the target stays on
 * the current origin, falling back to a normal browser navigation for any
 * external link.
 *
 * @param {string | null | undefined} actionUrl
 */
const navigateToNotificationAction = (actionUrl) => {
  if (!actionUrl || typeof window === "undefined") {
    return;
  }

  try {
    const url = new URL(actionUrl, window.location.origin);

    if (url.origin === window.location.origin) {
      navigate(`${url.pathname}${url.search}${url.hash}`);
      return;
    }
  } catch {
    // Fall back to a normal browser navigation if the URL is malformed.
  }

  window.location.assign(actionUrl);
};

const getIntegrationLabel = (notification) => {
  const storeName =
    notification.integration?.parent?.description ||
    notification.metadata?.storeName ||
    null;

  if (storeName) {
    return storeName;
  }

  return notification.integration?.description || null;
};

const getHistorySummary = (shownCount, totalCount) => {
  if (!totalCount) {
    return null;
  }

  if (totalCount > shownCount) {
    return `Showing latest ${shownCount} of ${totalCount} historical notifications`;
  }

  return `${totalCount} historical notification${totalCount === 1 ? "" : "s"}`;
};

const getHistoryTriggerSummary = (shownCount, totalCount) => {
  const count = totalCount || shownCount;

  if (!count) {
    return null;
  }

  return `${count} previous notification${count === 1 ? "" : "s"}`;
};

const getNotificationTitleIcon = (notification) => {
  if (notification.type === "DATA_IMPORT_COMPLETED") {
    return (
      <ArrowDownToLine
        className="text-primary size-4 shrink-0"
        data-testid="notification-title-icon-data_import_completed"
      />
    );
  }

  return null;
};

const shouldShowInlineNotificationAction = (notification) =>
  !!notification.actionUrl &&
  !notification.resolvedAt &&
  notification.type !== "DATA_IMPORT_COMPLETED";

const EmptyActiveState = ({ hasHistory = false }) => (
  <div className="flex flex-col items-center justify-center gap-3 px-3 py-6 text-center">
    <div className="bg-muted/20 text-muted-foreground/45 flex size-11 items-center justify-center rounded-full border border-dashed">
      <Bell className="size-5" strokeWidth={1.8} />
    </div>

    <div className="space-y-1">
      <div className="text-muted-foreground text-sm font-medium">
        Nothing new right now
      </div>
      <p className="text-muted-foreground/70 text-xs">
        {hasHistory
          ? "Recent history is tucked just below."
          : "New activity will appear here when something needs attention."}
      </p>
    </div>
  </div>
);

const NotificationItem = ({
  notification,
  compact = false,
  onMarkRead,
  onAction,
}) => {
  const [markingRead, setMarkingRead] = useState(false);
  const isResolved = !!notification.resolvedAt;
  const isRead = !!notification.readAt;
  const isPersistentIssue =
    notification.behavior === "PERSISTENT_ISSUE" && !isResolved;
  const canMarkRead =
    notification.behavior === "INFORMATIONAL" && !isRead && !isResolved;
  const storeIntegration =
    notification.integration?.parent ||
    (notification.integration?.type === "SHOPIFY"
      ? notification.integration
      : null);
  const relativeTimestamp = formatRelativeTimestamp(notification.createdAt);
  const integrationLabel = getIntegrationLabel(notification);
  const titleIcon = getNotificationTitleIcon(notification);
  const showInlineAction = shouldShowInlineNotificationAction(notification);
  const handleMarkRead = async () => {
    if (!onMarkRead || markingRead) {
      return;
    }

    setMarkingRead(true);

    try {
      await onMarkRead(notification.id);
    } finally {
      setMarkingRead(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-xl border p-3",
        (isPersistentIssue || (!isRead && !isResolved)) &&
          "border-primary/30 bg-primary/5"
      )}
    >
      <div className="min-w-0 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {titleIcon}
            <div className="min-w-0 font-medium leading-tight">
              {notification.title}
            </div>
          </div>
          {relativeTimestamp && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-muted-foreground shrink-0 text-xs">
                  {relativeTimestamp}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {formatTimestampTooltip(notification.createdAt)}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className="text-muted-foreground text-sm">{notification.body}</div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1 text-xs">
            {integrationLabel && (
              <div className="text-muted-foreground flex items-center gap-1.5">
                {storeIntegration && (
                  <StoreAvatar
                    integrationId={storeIntegration.id}
                    storeName={storeIntegration.description}
                    className="size-4 shrink-0 rounded-sm border text-[10px]"
                  />
                )}
                <span className="min-w-0 break-words">{integrationLabel}</span>
              </div>
            )}
          </div>

          {(canMarkRead || showInlineAction) && (
            <div className="flex shrink-0 items-center gap-2">
              {canMarkRead && (
                <Button
                  variant="ghost"
                  size={compact ? "sm" : "default"}
                  disabled={markingRead}
                  onClick={handleMarkRead}
                >
                  {markingRead && <Loader2 className="size-4 animate-spin" />}
                  Mark read
                </Button>
              )}

              {showInlineAction && (
                <Button
                  size={compact ? "sm" : "default"}
                  onClick={async () => {
                    if (!isPersistentIssue && !isRead) {
                      await onMarkRead?.(notification.id);
                    }

                    onAction?.(notification);
                    navigateToNotificationAction(notification.actionUrl);
                  }}
                >
                  {notification.actionLabel || "Fix now"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const NotificationList = ({
  activeNotifications = [],
  historyNotifications = [],
  historyCount = 0,
  compact = false,
  showActiveEmptyState = false,
  collapseHistoryByDefault = false,
  onMarkRead,
  onAction,
  className,
}) => {
  const historySummary = getHistorySummary(
    historyNotifications.length,
    historyCount
  );
  const historyTriggerSummary = getHistoryTriggerSummary(
    historyNotifications.length,
    historyCount
  );
  const hasNotifications =
    activeNotifications.length > 0 || historyNotifications.length > 0;
  const [historyOpen, setHistoryOpen] = useState(!collapseHistoryByDefault);

  return (
    <ScrollArea
      className={cn(
        compact ? "max-h-96" : "max-h-[32rem]",
        "flex flex-col -mr-3 pr-3",
        className
      )}
      type="always"
    >
      <div className="space-y-4">
        {!activeNotifications.length &&
          (showActiveEmptyState ? (
            <EmptyActiveState hasHistory={historyNotifications.length > 0} />
          ) : !hasNotifications ? (
            <div className="text-muted-foreground rounded-xl border border-dashed p-4 text-sm">
              No notifications right now.
            </div>
          ) : null)}

        {!!activeNotifications.length && (
          <div className="space-y-3">
            {activeNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                compact={compact}
                onMarkRead={onMarkRead}
                onAction={onAction}
              />
            ))}
          </div>
        )}

        {!!historyNotifications.length &&
          (collapseHistoryByDefault ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 px-1">
                <div className="bg-border h-px flex-1" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground h-7 rounded-full px-2.5 text-xs font-medium"
                  onClick={() => setHistoryOpen((open) => !open)}
                >
                  {historyOpen ? "Hide history" : "Show history"}
                  {historyTriggerSummary ? ` · ${historyTriggerSummary}` : ""}
                  <ChevronDown
                    className={cn(
                      "size-3.5 shrink-0 transition-transform",
                      historyOpen && "rotate-180"
                    )}
                  />
                </Button>
                <div className="bg-border h-px flex-1" />
              </div>

              {historyOpen && (
                <div className="space-y-3">
                  {historyNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      compact={compact}
                      onMarkRead={onMarkRead}
                      onAction={onAction}
                    />
                  ))}
                  {historySummary && (
                    <div className="text-muted-foreground px-1 text-xs">
                      {historySummary}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm font-semibold">History</div>
              {historyNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  compact={compact}
                  onMarkRead={onMarkRead}
                  onAction={onAction}
                />
              ))}
              {historySummary && (
                <div className="text-muted-foreground px-1 text-xs">
                  {historySummary}
                </div>
              )}
            </div>
          ))}
      </div>
    </ScrollArea>
  );
};
